import type { DraftState, KnownPairSummary, WizardSampleGroup, WizardStep } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { getSampleConfigurationLabel, getSequencingPlatformLabel } from '../../Util/sequencingPlatform.ts'
import { primersFor } from './sheet/applyPlan.ts'
import { currentPools, itemCounts, placedGroups, poolContents } from './pools.ts'
import { effectiveMetadata } from './sheet/sheetImport.ts'
import { archiveSamples, isArchiveOrder, sheetRowCounts } from './archive.ts'
import type { IssueList } from './issues.ts'
import { submitBlockedReason } from './issues.ts'
import { taxonLabel } from '../../View/Page/OrderWizard/wizardLabels.ts'
import { isBusy, submitChecksDue, uploadSummary } from './writeState.ts'
import { reviewSignature } from './reviewSignature.ts'
import { plural } from '../../Util/plural.ts'
import { pairLabel } from './discovery.ts'
import { writtenEstimate } from './primerOrientation.ts'

/** One line per step for when it needs nothing from the lab: what the wizard found and did. */
export const stepSummary = (draft: DraftState, step: WizardStep): string => {
  const groups = placedGroups(draft)
  switch (step) {
    case 'files': {
      const uploads = uploadSummary(draft)
      const discovery = draft.discovery
      const pair = discovery?.primers.status === 'found' ? discovery.primers.pair : undefined
      const estimates = groups.map((group) => writtenEstimate(draft, group))
      const pairName = (found: KnownPairSummary) => pairLabel(found, estimates).name ?? `${found.first} / ${found.second}`
      return [
        `${uploads.uploaded}/${uploads.total} files uploaded`,
        discovery?.platform && discovery.platform !== 'UNKNOWN' ? getSequencingPlatformLabel(discovery.platform) : undefined,
        pair ? `${pairName(pair)} → ${taxonLabel(draft.settings?.targetTaxonomy.value ?? pair.amplicons[0])}` : undefined,
        draft.settings ? getSampleConfigurationLabel(draft.settings.sampleConfiguration.value) : undefined
      ]
        .filter(Boolean)
        .join(' · ')
    }
    case 'pools': {
      if (isArchiveOrder(draft)) {
        const samples = Object.values(archiveSamples(draft)).flat()
        const controls = samples.filter((sample) => sample.itemType === 'NEGATIVE_CONTROL').length
        return itemCounts(samples.length - controls, controls)
      }
      const pools = poolContents(draft, currentPools(draft)).length
      return pools <= 1 ? 'One pool' : `${pools} pools`
    }
    case 'primers': {
      const sources = groups.map((group) => (group.primersOnServer ? 'server' : primersFor(draft, group).source))
      const count = (source: string) => sources.filter((value) => value === source).length
      const withPrimers = sources.filter(Boolean).length
      const parts = [
        count('sheet') > 0 ? `${count('sheet')} from the sheet` : undefined,
        count('estimate') > 0 ? `${count('estimate')} from the reads` : undefined,
        count('manual') > 0 ? `${count('manual')} entered` : undefined,
        count('server') > 0 ? `${count('server')} set in the order` : undefined
      ].filter(Boolean)
      return `${withPrimers}/${groups.length} with exact primers${parts.length > 0 ? ` (${parts.join(', ')})` : ''}`
    }
    case 'metadata': {
      const sheet = draft.sheet
      if (!sheet) {
        return draft.sheetRef ? `${draft.sheetRef.name}: drop it again` : 'No sample sheet'
      }
      const { matched } = sheetRowCounts(draft)
      const fields = Object.keys(effectiveMetadata(sheet.mapping, sheet.extraColumns)).length
      const custom = Object.values(sheet.extraColumns).filter((target) => target === 'custom').length
      return `${sheet.name}: ${plural(matched, 'row')} matched, ${plural(fields, 'metadata field')}, ${plural(custom, 'custom attribute')}`
    }
    case 'review':
      return plural(groups.length, 'item')
  }
}

/** The check's outcome for the draft as it is now; one for an earlier state of the draft no longer counts. */
export const currentSubmitFlow = (draft: DraftState) => {
  const flow = draft.submitFlow
  return flow && flow.stage !== 'checking' && flow.signature === reviewSignature(draft) ? flow : undefined
}

export const reviewChecking = (draft: DraftState, filesAtHand: (group: WizardSampleGroup) => boolean): boolean =>
  draft.submitFlow?.stage === 'checking' || (!currentSubmitFlow(draft) && submitChecksDue(draft, filesAtHand))

export type ReviewStatus = { stage: 'blocked' | 'checking' | 'passed' | 'waiting'; text: string }

/** Where Review stands over every step: what blocks submit first, then the check under way; "ready" only once it passed. */
export const reviewStatus = (
  draft: DraftState,
  issues: Pick<IssueList, 'blockers' | 'warnings'>,
  filesAtHand: (group: WizardSampleGroup) => boolean
): ReviewStatus => {
  const reason = submitBlockedReason(issues, isBusy(draft, filesAtHand))
  if (issues.blockers.length > 0 && reason) {
    return { stage: 'blocked', text: reason }
  }
  if (reviewChecking(draft, filesAtHand)) {
    return { stage: 'checking', text: 'Checking the order…' }
  }
  const items = plural(placedGroups(draft).length, 'item')
  const review = currentSubmitFlow(draft)?.review
  if (review && !reason && (review.platform?.issues ?? []).length === 0) {
    return { stage: 'passed', text: `${items} ready to submit` }
  }
  return { stage: 'waiting', text: reason ?? items }
}

/** The footer's line: the step's summary and its notes. */
export const stepSummaryLine = (draft: DraftState, issues: Pick<IssueList, 'infos'>, step: WizardStep): string =>
  [stepSummary(draft, step), ...issues.infos.filter((info) => info.step === step).map((info) => info.text)].join(' · ')
